import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router'
import { useAppData } from '../../data'
import {
  canonicalBillPath,
  canonicalSubject,
  explorerStatesEqual,
  explorerStateFromQueryPairs,
  explorerUrl,
  filterBills,
  defaultExplorerState,
} from '../../discovery'
import type { ExplorerState } from '../../discovery'
import { formatDateShort } from '../../helpers'
import { ShareButton } from '../ShareButton'
import { clearState, chamberLabel, statusLabel } from './filters'
import { chamberDisplayLabel, explorerIndexState, recentActivityLabel, subjectSummary } from './results'
import { uniqueSubjects, subjectSuggestions } from './subjectTypeahead'
import { visibleCountLabel, withVotesLabel } from './toolbar'

const EXPLORER_PAGE_SIZE = 50
const SEARCH_DEBOUNCE_MS = 160

export function ExplorerPageContent() {
  const appData = useAppData()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()

  // Initialize state from URL query params
  const [state, setState] = useState<ExplorerState>(() =>
    explorerStateFromQueryPairs([...searchParams.entries()])
  )

  // Mirror state in a ref synchronously during render so effects always read
  // the current value without needing state in their dep arrays.
  const stateRef = useRef(state)
  stateRef.current = state

  // URL → state sync: when the query string changes (e.g. browser back/forward),
  // parse new state from URL and update only if different from current state.
  // We use the stable string location.search rather than the searchParams object
  // (which gets a new identity every render) so this effect only re-runs when
  // the query string actually changes.
  useEffect(() => {
    const next = explorerStateFromQueryPairs([...new URLSearchParams(location.search).entries()])
    if (!explorerStatesEqual(stateRef.current, next)) {
      setState(next)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  // state → URL sync: whenever state changes, push the canonical URL.
  // Compare against current location to avoid redundant navigations.
  // We use location from window to avoid adding navigate/location to deps
  // (the navigate reference is stable in react-router 7).
  useEffect(() => {
    const current =
      window.location.pathname + window.location.search + window.location.hash
    const next = explorerUrl(state)
    if (current !== next) {
      navigate(next, { replace: true, preventScrollReset: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  // Derived: filtered bills from the index
  const filteredBills = useMemo(() => {
    if (appData.billsIndex.status !== 'ready') return []
    return filterBills(appData.billsIndex.data.bills, state)
  }, [appData.billsIndex, state])

  // Pagination: visible count resets when state changes
  const [visibleCount, setVisibleCount] = useState(EXPLORER_PAGE_SIZE)
  const prevStateRef = useRef(state)
  useEffect(() => {
    if (!explorerStatesEqual(prevStateRef.current, state)) {
      setVisibleCount(EXPLORER_PAGE_SIZE)
    }
    prevStateRef.current = state
  }, [state])

  const visibleBills = useMemo(
    () => filteredBills.slice(0, visibleCount),
    [filteredBills, visibleCount]
  )
  const totalCount = filteredBills.length
  const hasMore = visibleCount < totalCount

  function loadMore() {
    setVisibleCount((n) => n + EXPLORER_PAGE_SIZE)
  }

  // Subject typeahead
  const subjectOptions = useMemo(() => {
    if (appData.billsIndex.status !== 'ready') return []
    return uniqueSubjects(appData.billsIndex.data.bills)
  }, [appData.billsIndex])

  const [subjectOpen, setSubjectOpen] = useState(false)

  // queryInput is the live text in the search box; state.query lags behind by
  // the debounce interval. An external state change (e.g. back button) syncs
  // the input from state.query.
  const [queryInput, setQueryInput] = useState(() => state.query)

  // External URL change → sync queryInput from state.query (Rust: Effect that
  // reads state.get() and overwrites query_input when they differ)
  useEffect(() => {
    if (queryInput !== state.query) {
      setQueryInput(state.query)
    }
    // We intentionally read queryInput via closure only on state.query change.
    // Adding queryInput to deps would cause a loop; the asymmetric dependency
    // mirrors the Rust get_untracked approach.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.query])

  const matchingSubjects = useMemo(
    () => subjectSuggestions(subjectOptions, queryInput),
    [subjectOptions, queryInput]
  )

  // Subject canonicalization: once subjectOptions are available, canonicalize
  // state.subject to ensure it matches a real subject exactly.
  const subjectOptionsRef = useRef(subjectOptions)
  useEffect(() => {
    subjectOptionsRef.current = subjectOptions
  })
  useEffect(() => {
    if (subjectOptions.length === 0) return
    const current = stateRef.current.subject
    const next = current != null ? canonicalSubject(subjectOptions, current) : null
    if (current !== next) {
      setState((s) => ({ ...s, subject: next }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectOptions])

  // Debounce search input
  const debounceTickRef = useRef(0)

  // Cleanup refs for blur timer
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    return () => {
      if (blurTimerRef.current !== null) clearTimeout(blurTimerRef.current)
    }
  }, [])

  function handleSearchInput(ev: React.ChangeEvent<HTMLInputElement>) {
    const value = ev.currentTarget.value
    setQueryInput(value)
    setSubjectOpen(true)
    debounceTickRef.current += 1
    const myTick = debounceTickRef.current
    setTimeout(() => {
      if (debounceTickRef.current === myTick && stateRef.current.query !== value) {
        setState((s) => ({ ...s, query: value }))
      }
    }, SEARCH_DEBOUNCE_MS)
  }

  function handleSearchFocus() {
    appData.triggerBillsIndex()
    setSubjectOpen(true)
  }

  function handleSearchBlur() {
    if (blurTimerRef.current !== null) clearTimeout(blurTimerRef.current)
    blurTimerRef.current = setTimeout(() => {
      setSubjectOpen(false)
      blurTimerRef.current = null
    }, 150)
  }

  function handleSubjectSelect(subject: string) {
    setState((s) => ({ ...s, subject, query: '' }))
    setQueryInput('')
    setSubjectOpen(false)
  }

  // Filter helpers
  const hasActiveFilters = !explorerStatesEqual(state, defaultExplorerState())

  function clearFilters() {
    const next = clearState()
    setState(next)
    setQueryInput(next.query)
  }

  // bills index UI state
  const indexState = explorerIndexState(appData.billsIndex)

  return (
    <section className="explorer-page">
      <div className="explorer-shell">
        <header className="explorer-header">
          <div className="explorer-header-copy">
            <p className="explorer-kicker">Explora el Congreso</p>
            <h1>Busca, filtra y comparte proyectos de ley</h1>
            <p className="explorer-intro">
              Usa los filtros para recorrer el índice común de proyectos y comparte la URL exacta de tu búsqueda.
            </p>
          </div>
          <div className="explorer-searchbar">
            <label className="explorer-search explorer-filter-typeahead">
              <span>Buscar</span>
              <input
                type="search"
                autoComplete="off"
                placeholder="Boletín, título, tema o palabra clave"
                value={queryInput}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                onChange={handleSearchInput}
              />
              {subjectOpen && matchingSubjects.length > 0 && (
                <div className="explorer-typeahead-list">
                  <p className="explorer-typeahead-heading">Temas sugeridos</p>
                  <ul className="explorer-typeahead-options">
                    {matchingSubjects.map((subject) => (
                      <li key={subject}>
                        <button
                          type="button"
                          className="explorer-typeahead-option"
                          onMouseDown={(ev) => {
                            ev.preventDefault()
                            handleSubjectSelect(subject)
                          }}
                        >
                          {subject}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </label>
          </div>
        </header>

        <div className="explorer-layout">
          <aside className="explorer-filters">
            <div className="explorer-filter-group">
              <label htmlFor="explorer-status">Estado</label>
              <select
                id="explorer-status"
                value={state.status ?? ''}
                onChange={(ev) => {
                  const value = ev.currentTarget.value
                  setState((s) => ({
                    ...s,
                    status:
                      value === 'en-tramitacion' ? value
                      : value === 'publicado' ? value
                      : null,
                  }))
                }}
              >
                <option value="">Todos</option>
                <option value="en-tramitacion">En tramitación</option>
                <option value="publicado">Publicado</option>
              </select>
            </div>

            <div className="explorer-filter-group">
              <label htmlFor="explorer-chamber">Cámara / origen</label>
              <select
                id="explorer-chamber"
                value={state.chamber ?? ''}
                onChange={(ev) => {
                  const value = ev.currentTarget.value
                  setState((s) => ({
                    ...s,
                    chamber:
                      value === 'senado' ? value
                      : value === 'diputados' ? value
                      : null,
                  }))
                }}
              >
                <option value="">Todas</option>
                <option value="senado">Senado</option>
                <option value="diputados">Cámara de Diputados</option>
              </select>
            </div>

            <div className="explorer-filter-group">
              <label htmlFor="explorer-votes">Con votaciones</label>
              <select
                id="explorer-votes"
                value={
                  state.withVotes === true ? 'true'
                  : state.withVotes === false ? 'false'
                  : ''
                }
                onChange={(ev) => {
                  const value = ev.currentTarget.value
                  setState((s) => ({
                    ...s,
                    withVotes:
                      value === 'true' ? true
                      : value === 'false' ? false
                      : null,
                  }))
                }}
              >
                <option value="">Todas</option>
                <option value="true">Solo con votaciones</option>
                <option value="false">Sin votaciones</option>
              </select>
            </div>

            <div className="explorer-filter-group">
              <label htmlFor="explorer-sort">Orden</label>
              <select
                id="explorer-sort"
                value={state.sort}
                onChange={(ev) => {
                  const value = ev.currentTarget.value
                  setState((s) => ({
                    ...s,
                    sort: value === 'newest-filed' ? 'newest-filed' : 'recent-activity',
                  }))
                }}
              >
                <option value="recent-activity">Actividad reciente</option>
                <option value="newest-filed">Más recientes de ingreso</option>
              </select>
            </div>
          </aside>

          <div className="explorer-results">
            {indexState.kind === 'loading' && (
              <div className="explorer-loading">
                <h2>Cargando índice de proyectos</h2>
                <p>
                  Estamos preparando el explorador. En cuanto termine la carga, verás el feed completo con filtros y resultados.
                </p>
              </div>
            )}

            {indexState.kind === 'error' && (
              <div className="explorer-error">
                <h2>No pudimos cargar el índice</h2>
                <p>Revisa tu conexión y vuelve a intentar cargar los proyectos.</p>
                <p className="explorer-error-detail">{indexState.error}</p>
                <button
                  type="button"
                  className="explorer-reset"
                  onClick={() => {
                    appData.refetchBillsIndex()
                  }}
                >
                  Reintentar
                </button>
              </div>
            )}

            {indexState.kind === 'ready' && (
              <div>
                <div className="explorer-toolbar">
                  <div className="explorer-active-filters">
                    {state.query.trim() !== '' && (
                      <button
                        type="button"
                        className="explorer-chip"
                        onClick={() => setState((s) => ({ ...s, query: '' }))}
                      >
                        {`Búsqueda: ${state.query}`}
                      </button>
                    )}
                    {state.status != null && (
                      <button
                        type="button"
                        className="explorer-chip"
                        onClick={() => setState((s) => ({ ...s, status: null }))}
                      >
                        {`Estado: ${statusLabel(state.status)}`}
                      </button>
                    )}
                    {state.subject != null && (
                      <button
                        type="button"
                        className="explorer-chip"
                        onClick={() => setState((s) => ({ ...s, subject: null }))}
                      >
                        {`Tema: ${state.subject}`}
                      </button>
                    )}
                    {state.chamber != null && (
                      <button
                        type="button"
                        className="explorer-chip"
                        onClick={() => setState((s) => ({ ...s, chamber: null }))}
                      >
                        {`Cámara: ${chamberLabel(state.chamber)}`}
                      </button>
                    )}
                    {state.withVotes != null && (
                      <button
                        type="button"
                        className="explorer-chip"
                        onClick={() => setState((s) => ({ ...s, withVotes: null }))}
                      >
                        {withVotesLabel(state)}
                      </button>
                    )}
                    {state.sort === 'newest-filed' && (
                      <button
                        type="button"
                        className="explorer-chip"
                        onClick={() => setState((s) => ({ ...s, sort: 'recent-activity' }))}
                      >
                        Orden: más recientes de ingreso
                      </button>
                    )}
                    {hasActiveFilters && (
                      <button
                        type="button"
                        className="explorer-clear-all"
                        title="Limpiar todos los filtros"
                        aria-label="Limpiar todos los filtros"
                        onClick={clearFilters}
                      >
                        <svg
                          className="explorer-clear-all-icon"
                          viewBox="0 0 20 20"
                          width="14"
                          height="14"
                          aria-hidden="true"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.75"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M3 4h14l-5.5 7v5l-3-1.5V11L3 4z" />
                          <path d="M4 16L16 4" />
                        </svg>
                        <span>Limpiar todo</span>
                      </button>
                    )}
                  </div>
                  <p className="explorer-count">
                    {visibleCountLabel(visibleBills.length, totalCount)}
                  </p>
                </div>

                {totalCount === 0 ? (
                  <div className="explorer-empty">
                    <h2>No encontramos coincidencias</h2>
                    <p>
                      Prueba quitando un filtro, cambiando el orden o limpiando la búsqueda para volver al feed completo.
                    </p>
                    <button type="button" className="explorer-reset" onClick={clearFilters}>
                      Volver a ver todo
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="explorer-results-grid">
                      {visibleBills.map((bill) => {
                        const filingDate = formatDateShort(bill.filing_date)
                        const chamber = chamberDisplayLabel(bill.origin_chamber)
                        const votesLabel = bill.has_votes ? 'Con votaciones' : 'Sin votaciones'
                        const subjects = subjectSummary(bill)
                        const recentActivity = recentActivityLabel(bill)
                        const isPublished = bill.status.includes('Publicado')
                        const statusClass = isPublished
                          ? 'bill-status--published'
                          : 'bill-status--pending'
                        const statusText =
                          bill.law_number != null && bill.law_number !== ''
                            ? bill.law_number
                            : bill.status

                        return (
                          <Link
                            key={bill.boletin}
                            to={canonicalBillPath(bill.boletin)}
                            className="explorer-card"
                          >
                            <div className="explorer-card-top">
                              <div className="explorer-card-badges">
                                <span className="bill-badge">{bill.boletin}</span>
                                <span className={`bill-status ${statusClass}`}>{statusText}</span>
                              </div>
                              <div className="explorer-card-actions">
                                {bill.has_votes && (
                                  <span className="explorer-card-votes">{votesLabel}</span>
                                )}
                                <ShareButton boletin={bill.boletin} />
                              </div>
                            </div>
                            <h3 className="explorer-card-title">{bill.title}</h3>
                            <div className="explorer-card-meta">
                              <span>{filingDate}</span>
                              <span>{chamber}</span>
                              <span>{subjects}</span>
                              {recentActivity != null && <span>{recentActivity}</span>}
                            </div>
                          </Link>
                        )
                      })}
                    </div>
                    {hasMore && (
                      <div className="explorer-load-more">
                        <button
                          type="button"
                          className="explorer-load-more-button"
                          onClick={loadMore}
                        >
                          Cargar más proyectos
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
